import React from 'react';
import {
    FiArrowLeft,
    FiCheck,
    FiMapPin,
    FiMaximize2,
    FiPhone,
    FiShield,
    FiUsers,
    FiZap,
} from 'react-icons/fi';

const PortfolioDetailsContent = ({ data }) => {
    const specs = [
        { icon: <FiUsers aria-hidden="true" />, label: 'Pasażerowie', value: data.capacity },
        { icon: <FiMaximize2 aria-hidden="true" />, label: 'Długość', value: data.length },
        { icon: <FiZap aria-hidden="true" />, label: 'Silnik', value: data.engine },
    ];

    return (
        <div className="boat-detail">
            <section className="boat-detail__hero" aria-labelledby="boat-title">
                <div className="container">
                    <a className="boat-detail__back" href="/#pricing">
                        <FiArrowLeft aria-hidden="true" />
                        <span>Wróć do floty</span>
                    </a>

                    <div className="boat-detail__hero-grid">
                        <div className="boat-detail__intro">
                            <p className="boat-detail__eyebrow">{data.eyebrow}</p>
                            <h1 id="boat-title">{data.title}</h1>
                            <p className="boat-detail__lead">{data.shortDescription}</p>

                            <div className="boat-detail__location">
                                <FiMapPin aria-hidden="true" />
                                <span>{data.locationText}</span>
                            </div>

                            <div className="boat-detail__specs" aria-label="Najważniejsze parametry łodzi">
                                {specs.map(({ icon, label, value }) => (
                                    <div className="boat-detail__spec" key={label}>
                                        <span className="boat-detail__spec-icon">{icon}</span>
                                        <span>
                                            <small>{label}</small>
                                            <strong>{value}</strong>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="boat-detail__pricing" aria-label="Cennik wynajmu">
                            <span className="boat-detail__pricing-label">Cennik wynajmu</span>
                            <div className="boat-detail__price-row">
                                <span>2 godziny</span>
                                <strong>{data.date}</strong>
                            </div>
                            <div className="boat-detail__price-row">
                                <span>4 godziny</span>
                                <strong>{data.client}</strong>
                            </div>
                            <p className="boat-detail__pricing-note">
                                Termin i wariant wynajmu potwierdzamy telefonicznie. Dostępny jest również czarter z doświadczonym sternikiem.
                            </p>
                            <a className="boat-detail__cta" href={data.btn.link}>
                                <FiPhone aria-hidden="true" />
                                <span>{data.btn.buttontext}: +48 668 650 693</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="boat-detail__visual" aria-label={`Zdjęcie ${data.title}`}>
                <div className="container">
                    <div className="boat-detail__cover">
                        <img
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                            src={`${process.env.PUBLIC_URL}/${data.largeImage.replace(/^\.\//, '')}`}
                            alt={`${data.title} — motorówka BlackWave na wynajem w Augustowie`}
                        />
                    </div>
                </div>
            </section>

            <section className="boat-detail__content rn-section-gap" aria-labelledby="boat-equipment">
                <div className="container">
                    <div className="boat-detail__content-grid">
                        <div>
                            <p className="boat-detail__section-kicker">Komfort na wodzie</p>
                            <h2 id="boat-equipment">Wyposażenie {data.title}</h2>
                            <p>
                                Jednostka została przygotowana do komfortowych rejsów po augustowskich jeziorach.
                                Przed wypłynięciem przekazujemy najważniejsze informacje dotyczące obsługi i bezpieczeństwa.
                            </p>

                            <ul className="boat-detail__checklist">
                                {data.equipment.map((item) => (
                                    <li key={item}>
                                        <FiCheck aria-hidden="true" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <aside className="boat-detail__included" aria-labelledby="included-title">
                            <div className="boat-detail__included-icon"><FiShield aria-hidden="true" /></div>
                            <p className="boat-detail__section-kicker">W cenie wynajmu</p>
                            <h2 id="included-title">Przejrzyste zasady</h2>
                            <ul>
                                {data.included.map((item) => (
                                    <li key={item}>
                                        <FiCheck aria-hidden="true" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="boat-detail__included-footnote">
                                Szczegółowe warunki i aktualną dostępność zawsze potwierdzamy przy rezerwacji.
                            </p>
                        </aside>
                    </div>
                </div>
            </section>

            <section className="boat-detail__gallery rn-section-gap" aria-labelledby="boat-gallery-title">
                <div className="container">
                    <div className="boat-detail__gallery-heading">
                        <div>
                            <p className="boat-detail__section-kicker">Zobacz jednostkę</p>
                            <h2 id="boat-gallery-title">Galeria {data.title}</h2>
                        </div>
                        <a href={data.btn.link} className="boat-detail__gallery-call">
                            Sprawdź dostępny termin
                            <FiPhone aria-hidden="true" />
                        </a>
                    </div>

                    <div className="boat-detail__gallery-grid">
                        {[data.gallery.imageOne, data.gallery.imageTwo, data.gallery.imageThree].map((image, index) => (
                            <figure className={`boat-detail__gallery-item boat-detail__gallery-item--${index + 1}`} key={image}>
                                <img
                                    loading="lazy"
                                    decoding="async"
                                    src={`${process.env.PUBLIC_URL}/${image.replace(/^\.\//, '')}`}
                                    alt={`${data.title} — zdjęcie ${index + 1} z galerii BlackWave Augustów`}
                                />
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            <section className="boat-detail__final-cta" aria-labelledby="reserve-boat-title">
                <div className="container">
                    <div className="boat-detail__final-card">
                        <div>
                            <p className="boat-detail__section-kicker">BlackWave Augustów</p>
                            <h2 id="reserve-boat-title">Chcesz zarezerwować {data.title}?</h2>
                            <p>Zadzwoń i sprawdź dostępność wybranego terminu. Pomożemy dobrać wariant rejsu do liczby osób i planu dnia.</p>
                        </div>
                        <a className="boat-detail__cta boat-detail__cta--light" href={data.btn.link}>
                            <FiPhone aria-hidden="true" />
                            <span>+48 668 650 693</span>
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PortfolioDetailsContent;
